import React, {useMemo, useEffect, useState} from 'react';
import * as turf from '@turf/turf';
import {Button} from 'primereact/button';
import {ProgressSpinner} from 'primereact/progressspinner';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';


import MapWrapper from '../components/chart/MapWrapper';
import AngleDial from '../components/chart/AngleDial';
import ControlBar from './global/ControlBar';
import {useAppDispatch, useAppSelector} from '../redux/hooks';
import {setSelectLine, setOffset, setSelectedDrillholeId} from '../redux/slices/ChartStateSlice';
import {useDrillholeData} from '../components/hooks/useDrillholeData';
import {useDrillholeDetails} from '../components/hooks/useDrillholeDetails';
import {useTemplates} from '../components/hooks/useTemplates';

// ── helpers ────────────────────────────────────────────────────────────────

function bearingFromLine(x1: number, y1: number, x2: number, y2: number): number {
    const b = turf.bearing(turf.point([x1, y1]), turf.point([x2, y2]));
    return ((b % 360) + 360) % 360;
}

function lengthFromLine(x1: number, y1: number, x2: number, y2: number): number {
    return Math.max(10, Math.round(
        turf.distance(turf.point([x1, y1]), turf.point([x2, y2]), {units: 'kilometers'}) * 1000
    ));
}

// ── component ──────────────────────────────────────────────────────────────

const GeoMap = () => {
    const {withCoords, withoutCoords, isLoading} = useDrillholeData();
    const {fetchDetails} = useDrillholeDetails();
    const {selectLine, offset, selectedDrillholeId, isControlBarCollapsed} = useAppSelector((state: any) => state.oldDesignState);
    const dispatch = useAppDispatch();

    const {templates, addTemplate, updateTemplate, deleteTemplate} = useTemplates<{
        lineStartPoint: [number, number] | null,
        lineAngle: number,
        lineLength: number
    }>('geomap_templates');

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [isAddTemplateDialogVisible, setIsAddTemplateDialogVisible] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');

    // ── line tool state (initialised from persisted Redux / localStorage) ──
    const [lineStartPoint, setLineStartPoint] = useState<[number, number] | null>(() => {
        if (selectLine && selectLine.x1 != null) return [selectLine.x1, selectLine.y1];
        return null;
    });

    const [lineAngle, setLineAngle] = useState<number>(() => {
        if (selectLine && selectLine.x1 != null)
            return bearingFromLine(selectLine.x1, selectLine.y1, selectLine.x2, selectLine.y2);
        return 0;
    });

    const [lineLength, setLineLength] = useState<number>(() => {
        if (selectLine && selectLine.x1 != null)
            return lengthFromLine(selectLine.x1, selectLine.y1, selectLine.x2, selectLine.y2);
        return 500;
    });

    const [isPickingStartPoint, setIsPickingStartPoint] = useState(false);
    const [zoomToLineTrigger, setZoomToLineTrigger] = useState(0);
    const [hasInitialZoomed, setHasInitialZoomed] = useState(false);

    // ── zoom to line on mount if it exists ──────────────────────────────────
    useEffect(() => {
        if (!hasInitialZoomed && selectLine && selectLine.x1 != null && withCoords.length > 0) {
            const timer = setTimeout(() => {
                setZoomToLineTrigger(v => v + 1);
                setHasInitialZoomed(true);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [selectLine, withCoords.length, hasInitialZoomed]);

    // ── deselect template if values change ─────────────────────────────────
    useEffect(() => {
        if (selectedTemplateId) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                const isSamePoint = template.data.lineStartPoint && lineStartPoint &&
                    template.data.lineStartPoint[0] === lineStartPoint[0] &&
                    template.data.lineStartPoint[1] === lineStartPoint[1];

                const isSameAngle = template.data.lineAngle === lineAngle;
                const isSameLength = template.data.lineLength === lineLength;

                if (!isSamePoint || !isSameAngle || !isSameLength) {
                    setSelectedTemplateId(null);
                }
            } else {
                setSelectedTemplateId(null);
            }
        }
    }, [lineStartPoint, lineAngle, lineLength, templates, selectedTemplateId]);

    // ── compute line from start + angle + length ───────────────────────────
    const computedSelectLine = useMemo<[[number, number], [number, number]] | null>(() => {
        if (!lineStartPoint || lineLength <= 0 || isNaN(lineAngle) || isNaN(lineLength)) return null;
        try {
            const end = turf.destination(
                turf.point(lineStartPoint),
                lineLength / 1000,
                lineAngle,
                {units: 'kilometers'}
            );
            return [lineStartPoint, end.geometry.coordinates as [number, number]];
        } catch {
            return null;
        }
    }, [lineStartPoint, lineAngle, lineLength]);

    const lineCapturedIds = useMemo<Set<string>>(() => {
        if (!computedSelectLine || !offset || offset < 1 || withCoords.length === 0) return new Set();
        try {
            const ls = turf.lineString(computedSelectLine);
            const buff = turf.buffer(ls, offset / 1.6, {units: 'meters'});
            if (!buff) return new Set();
            const ids = new Set<string>();
            for (const bh of withCoords) {
                if (!bh.collar || bh.collar.LONGITUDE == null || bh.collar.LATITUDE == null) continue;
                if (turf.booleanPointInPolygon(turf.point([bh.collar.LONGITUDE, bh.collar.LATITUDE]), buff)) {
                    ids.add(bh.hole_id);
                }
            }
            return ids;
        } catch {
            return new Set();
        }
    }, [computedSelectLine, offset, withCoords]);

    // ── persist computed line to Redux / localStorage ──────────────────────
    useEffect(() => {
        if (computedSelectLine) {
            dispatch(setSelectLine({
                x1: computedSelectLine[0][0],
                y1: computedSelectLine[0][1],
                x2: computedSelectLine[1][0],
                y2: computedSelectLine[1][1],
            }));
        }
    }, [computedSelectLine]);

    // ── callbacks ──────────────────────────────────────────────────────────

    const handleSelectLineChanged = (sL: any[], sB: any[]) => {
        if (sL.length === 2) {
            fetchDetails(sB, [sL[0][0], sL[0][1]]);
        }
    };

    const handleStartPointPicked = (coord: [number, number]) => {
        setLineStartPoint(coord);
        setIsPickingStartPoint(false);
    };

    const handleLineDragged = (newStart: [number, number]) => {
        setLineStartPoint(newStart);
    };

    const handleLineAngleChanged = (newAngle: number) => {
        setLineAngle(Math.round(newAngle * 10) / 10);
    };

    const handleLineLengthChanged = (newLength: number) => {
        setLineLength(newLength);
    };

    const handleApplyTemplate = (id: string) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setLineStartPoint(template.data.lineStartPoint);
            setLineAngle(template.data.lineAngle);
            setLineLength(template.data.lineLength);
            setSelectedTemplateId(id);
        }
    };

    const handleSaveNewTemplate = async () => {
        if (newTemplateName.trim()) {
            const existing = templates.find(t => t.name.toLowerCase() === newTemplateName.trim().toLowerCase());
            if (existing) {
                await updateTemplate(existing.id, {lineStartPoint, lineAngle, lineLength});
                setSelectedTemplateId(existing.id);
            } else {
                const newTpl = await addTemplate(newTemplateName, {lineStartPoint, lineAngle, lineLength});
                if (newTpl) setSelectedTemplateId(newTpl.id);
            }
            setNewTemplateName('');
            setIsAddTemplateDialogVisible(false);
        }
    };

    const handleDeleteTemplate = () => {
        if (selectedTemplateId) {
            deleteTemplate(selectedTemplateId);
            setSelectedTemplateId(null);
        }
    };

    const handleSingleDrillholeSelect = (drillhole: any) => {
        dispatch(setSelectedDrillholeId(drillhole.hole_id));

        const collar = drillhole.collar;
        const lon = collar?.LONGITUDE ?? 0;
        const lat = collar?.LATITUDE ?? 0;
        const totalDepth = collar?.TOTALDEPTH ?? 100;

        const latRad = (lat * Math.PI) / 180;
        const halfDeg = (totalDepth / 2) / (111320 * Math.cos(latRad));
        const newStart: [number, number] = [lon - halfDeg, lat];
        const newEnd: [number, number] = [lon + halfDeg, lat];

        setLineStartPoint(newStart);
        setLineAngle(bearingFromLine(newStart[0], newStart[1], newEnd[0], newEnd[1]));
        setLineLength(lengthFromLine(newStart[0], newStart[1], newEnd[0], newEnd[1]));

        fetchDetails([drillhole], [newStart[0], newStart[1]]);
    };

    const mapSelectLine: any[] = computedSelectLine
        ?? (selectLine?.x1 != null
            ? [[selectLine.x1, selectLine.y1], [selectLine.x2, selectLine.y2]]
            : []);

    const lineControls = (
        <div className="w-full">
            <Button
                label={isPickingStartPoint ? 'Cancel' : 'Pick start point'}
                icon={isPickingStartPoint ? 'pi pi-times' : 'pi pi-map-marker'}
                className={`p-button-sm w-full mb-2${isPickingStartPoint ? ' p-button-danger' : ' p-button-outlined'}`}
                onClick={() => setIsPickingStartPoint(v => !v)}
            />

            <div style={{
                fontSize: '10px',
                color: 'var(--text-color-secondary)',
                marginBottom: '12px',
                fontFamily: 'monospace',
                minHeight: '15px',
            }}>
                {lineStartPoint ? `${lineStartPoint[1].toFixed(5)}, ${lineStartPoint[0].toFixed(5)}` : ''}
            </div>

            <div style={{marginBottom: '12px'}}>
                <div style={{fontSize: '11px', color: 'var(--text-color-secondary)', marginBottom: '6px'}}>Angle</div>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <AngleDial value={lineAngle} onChange={setLineAngle} size={88}/>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <input
                            type="number"
                            value={lineAngle}
                            min={0}
                            max={359.9}
                            step={0.1}
                            style={{
                                width: '62px',
                                background: 'var(--surface-ground)',
                                border: '1px solid var(--surface-border)',
                                color: 'var(--text-color)',
                                borderRadius: '4px',
                                padding: '3px 6px',
                                fontSize: '13px',
                            }}
                            onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v)) setLineAngle(((v % 360) + 360) % 360);
                            }}
                        />
                        <span style={{fontSize: '10px', color: 'var(--text-color-secondary)'}}>Degrees (°)</span>
                    </div>
                </div>
            </div>

            <div style={{display: 'flex', gap: '12px', marginBottom: '10px'}}>
                <div style={{flex: 1}}>
                    <div style={{fontSize: '11px', color: 'var(--text-color-secondary)', marginBottom: '4px'}}>Length</div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <input
                            type="number"
                            value={lineLength}
                            min={10}
                            style={{
                                width: '72px',
                                background: 'var(--surface-ground)',
                                border: '1px solid var(--surface-border)',
                                color: 'var(--text-color)',
                                borderRadius: '4px',
                                padding: '3px 6px',
                                fontSize: '13px',
                            }}
                            onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v) && v > 0) setLineLength(v);
                            }}
                        />
                        <span style={{fontSize: '10px', color: 'var(--text-color-secondary)'}}>m</span>
                    </div>
                </div>
                <div style={{flex: 1}}>
                    <div style={{fontSize: '11px', color: 'var(--text-color-secondary)', marginBottom: '4px'}}>Offset</div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <input
                            type="number"
                            value={offset}
                            min={1}
                            style={{
                                width: '72px',
                                background: 'var(--surface-ground)',
                                border: '1px solid var(--surface-border)',
                                color: 'var(--text-color)',
                                borderRadius: '4px',
                                padding: '3px 6px',
                                fontSize: '13px',
                            }}
                            onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v) && v >= 1) dispatch(setOffset(v));
                            }}
                        />
                        <span style={{fontSize: '10px', color: 'var(--text-color-secondary)'}}>m</span>
                    </div>
                </div>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px'}}>
                <Button
                    label="Zoom to line"
                    icon="pi pi-search-plus"
                    className="p-button-sm p-button-outlined w-full"
                    disabled={!lineStartPoint}
                    onClick={() => setZoomToLineTrigger(v => v + 1)}
                />
                <Button
                    label="Clear line"
                    icon="pi pi-trash"
                    className="p-button-sm p-button-text p-button-danger w-full"
                    disabled={!lineStartPoint}
                    onClick={() => {
                        setLineStartPoint(null);
                        dispatch(setSelectLine(null));
                    }}
                />
            </div>
        </div>
    );

    // ── render ─────────────────────────────────────────────────────────────
    return (
        <div className="card w-full h-full">
            <Dialog header="Save Template" visible={isAddTemplateDialogVisible} style={{width: '300px'}} onHide={() => setIsAddTemplateDialogVisible(false)}>
                <div className="flex flex-column gap-2">
                    <label htmlFor="templateName">Name</label>
                    <Dropdown id="templateName"
                              value={newTemplateName}
                              options={templates}
                              optionLabel="name"
                              optionValue="name"
                              onChange={(e) => setNewTemplateName(e.value)}
                              editable
                              placeholder="Enter or select name"
                              className="p-inputtext-sm"
                              autoFocus
                    />
                </div>
                <div className="flex justify-content-end mt-4">
                    <Button label="Cancel" icon="pi pi-times" onClick={() => setIsAddTemplateDialogVisible(false)} className="p-button-text p-button-secondary mr-2"/>
                    <Button label="Save" icon="pi pi-check" onClick={handleSaveNewTemplate} disabled={!newTemplateName.trim()}/>
                </div>
            </Dialog>

            <div style={{display: 'flex', height: '100%'}}>
                <div style={{flexShrink: 0, width: isControlBarCollapsed ? '36px' : '320px', overflow: isControlBarCollapsed ? 'hidden' : 'auto', transition: 'width 0.2s'}}>
                    <ControlBar
                        display={[]}
                        setDisplay={() => {}}
                        showLegend={false}
                        showDepths={false}
                        showScale={false}
                        templates={templates}
                        selectedTemplateId={selectedTemplateId}
                        onApplyTemplate={handleApplyTemplate}
                        onSaveTemplate={() => {
                            const selected = templates.find(t => t.id === selectedTemplateId);
                            setNewTemplateName(selected ? selected.name : '');
                            setIsAddTemplateDialogVisible(true);
                        }}
                        onDeleteTemplate={handleDeleteTemplate}
                        showMenuCard={true}
                        menuCardContent={lineControls}
                        extraControls={
                            <div className="w-full">
                                <h4>Boreholes</h4>
                                <div style={{maxHeight: '50vh', overflowY: 'auto'}}>
                                    {[...withCoords, ...withoutCoords].map((dh: any) => {
                                        const hasCoords = dh.collar?.LONGITUDE != null && dh.collar?.LATITUDE != null;
                                        const isCaptured = lineCapturedIds.has(dh.hole_id);
                                        return (
                                            <div
                                                key={dh.hole_id}
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '13px',
                                                    background: isCaptured ? 'rgba(255,102,0,0.12)' : 'transparent',
                                                    color: hasCoords ? 'var(--text-color)' : 'var(--text-color-secondary)',
                                                    borderLeft: isCaptured ? '3px solid #ff6600' : '3px solid transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                            >
                                                <span>{dh.hole_id}</span>
                                                {!hasCoords && (
                                                    <span style={{fontSize: '10px', color: 'var(--text-color-secondary)', fontStyle: 'italic'}}>no coords</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        }
                    />
                </div>
                <div style={{flex: '1 1 0', overflow: 'hidden', minWidth: 0}}>
                    <div style={{width: '100%', height: '100%', position: 'relative'}}>
                        {isLoading && <ProgressSpinner className="progress-spinner"/>}

                        {withCoords.length > 0 && offset && (
                            <MapWrapper
                                boreholes={withCoords}
                                selectLine={mapSelectLine}
                                offset={offset}
                                onSelectLineChanged={handleSelectLineChanged}
                                capturedBoreholeIds={lineCapturedIds}
                                pickStartPointMode={isPickingStartPoint}
                                onStartPointPicked={handleStartPointPicked}
                                onLineDragged={handleLineDragged}
                                onLineAngleChanged={handleLineAngleChanged}
                                onLineLengthChanged={handleLineLengthChanged}
                                zoomToLineTrigger={zoomToLineTrigger}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeoMap;
